import { put, take, fork, takeEvery, call, select, all, cancel, delay, spawn } from 'redux-saga/effects';
import * as actions from './actions.js';
import * as router from '../router/index.js';
import firestoreRedux from '@dreamworld/firestore-redux';
import get from 'lodash-es/get.js';
import isEmpty from 'lodash-es/isEmpty.js';
import forEach from 'lodash-es/forEach.js';
import { requestApi, isNetworkError } from "../../request-api.js";

/**
 * Load board setting details.
 */
function* load(action) {
  const state = yield select();
  const boardId = action && action.boardId || router.selectors.pageBoardId(state);
  const requesterId = action && action.requesterId || `translatation-details-${boardId}`;
  //Load translate boards details.
  try {
    const attrsQuery = firestoreRedux.getDocById('boards', boardId, { requesterId });
    const attrs = yield attrsQuery.result;
    const baseBoardId = get(attrs, 'baseBoardId');
    const id = baseBoardId || boardId;
    const response = yield call(requestApi, `/board/boards/${id}/translated`, { excludeErrors: [502] });
    yield put(actions._loadDone(id, response));
    
    forEach(response, (item) => {
      if(item && item.id) {
        try { firestoreRedux.getDocById('boards', item.id, { requesterId }); } catch (error) {}
      }
    });
    
    // Load base board details.
    if(baseBoardId) {
      try {
        const baseBoardQuery = firestoreRedux.getDocById('boards', baseBoardId, { requesterId });
        yield baseBoardQuery.result;
      } catch (error) {}
    }

  } catch (error) {}
}

function* disconnect(action) {
  const state = yield select();
  const boardId = action && action.boardId || router.selectors.pageBoardId(state);
  const requesterId = action && action.requesterId || `translatation-details-${boardId}`;
  firestoreRedux.cancelQueryByRequester(requesterId);
}

function* translateBoard(action) {
  const state = yield select();
  const boardId = action.boardId || router.selectors.pageBoardId(state);
  const languages = action.languages || [];
  if(!boardId || isEmpty(languages)) {
    console.error("translateBoard > failed due to this: boardId or languages are not found.", {boardId, languages});
    return;
  }

  try {
    const response = yield call(requestApi, `/board/boards/${boardId}/translate`, { method: 'POST', body: { languages }, excludeErrors: [502] });
    yield put(actions._translateBoardDone(boardId, languages, response));
  } catch (error) {
    yield put(actions._translateBoardFailed(boardId, languages, error && error.code || 'UNKNOWN'));
    if(isNetworkError(error)) {
      return;
    }
    console.error("translateBoard > failed due to this: ", error);
  }
}

function* reTranslateBoards(action) {
  const state = yield select();
  const boardId = action.boardId || router.selectors.pageBoardId(state);
  const languages = action.languages || [];
  if(!boardId) {
    console.error("reTranslateBoards > failed due to this: boardId is not found.", {boardId});
    return;
  }

  try {
    const response = yield call(requestApi, `/board/boards/${boardId}/re-translate-multiple`, { method: 'POST', body: { languages }, excludeErrors: [502] });
    yield put(actions._reTranslateBoardsDone(boardId, response));
  } catch (error) {
    yield put(actions._reTranslateBoardsFailed(boardId, error && error.code || 'UNKNOWN'));
    if(isNetworkError(error)) {
      return;
    }
    console.error("reTranslateBoards > failed due to this: ", error);
  }
}

function* retranslateBoard(action) {
  const state = yield select();
  const currentBoardId = router.selectors.pageBoardId(state);
  const boardId = action.boardId || currentBoardId;
  if(!boardId) {
    console.error("retranslateBoard > failed due to this: boardId is not found.", {boardId});
    return;
  }

  try {
    const response = yield call(requestApi, `/board/boards/${boardId}/re-translate`, { method: 'POST', excludeErrors: [502] });
    yield put(actions._retranslateBoardDone(boardId, response));
    if(boardId === currentBoardId) {
      router.actions.navigate(`/${response.accountId}/board/${response.id}`, true);
    }
  } catch (error) {
    yield put(actions._retranslateBoardFailed(boardId, error && error.code || 'UNKNOWN'));
    if(isNetworkError(error)) {
      return;
    }
    console.error("retranslateBoard > failed due to this: ", error);
  }
}

function* updateUseCaseBoardSettings(action) {
  const { boardId, settings } = action;
  if(!boardId || !settings) {
    console.error('Update use case board settings > empty boardId or settings passed.', { boardId, settings });
    return;
  }

  try {
    yield call(requestApi, `/board/use-case-boards/${boardId}`, { method: 'PUT', body: settings });
    yield put(actions._updateUseCaseBoardSettingsDone(boardId, settings));
    showSnackBar({ message: i18next.t('use-case-board:settings.toast') });
  } catch (error) {
    const code = error && error.code || 'UNKNOWN';
    yield put(actions._updateUseCaseBoardSettingsFailed(boardId, settings, code));
    if(isNetworkError(error)){
      return;
    }
    console.error('Update use case board settings > failed due to this: ', error);
  }
}

/**
 * Watch router change.
 */
function* watchRouter() {
  yield call(routeChangeHandler);
  yield takeEvery(router.actions.UPDATE_ROUTER, routeChangeHandler);
}

let previousDialog;
function* routeChangeHandler() {
  const state = yield select();
  const currentDialog = get(state, 'router.dialog.name');
  const currentPageName = get(state, 'router.page.name');
  if (currentDialog === 'TRANSLATIONS_DIALOG') {
    if (previousDialog !== currentDialog && currentPageName === 'BOARD') {
      yield put({ type: actions.DIALOG_OPENED });
    }
  } else if (previousDialog === 'TRANSLATIONS_DIALOG') {
    yield put({ type: actions.DIALOG_CLOSED });
  }

  if(currentDialog === 'USE_CASE_BOARD_SETTINGS') {
    if (previousDialog !== currentDialog && currentPageName === 'BOARD') {
      yield put({ type: actions.BOARD_SETTINGS_USE_CASE_BOARD_DIALOG_OPENED });
    }
  } else if (previousDialog === 'USE_CASE_BOARD_SETTINGS') {
    yield put({ type: actions.BOARD_SETTINGS_USE_CASE_BOARD_DIALOG_CLOSED });
  }
  previousDialog = currentDialog;
}

function* useCaseBoardSettingFlow() {
  while (yield take(actions.BOARD_SETTINGS_USE_CASE_BOARD_DIALOG_OPENED)) {
    const task = yield fork(function* () {
      yield all([
        call(load)
      ]);
    });
    yield take(actions.BOARD_SETTINGS_USE_CASE_BOARD_DIALOG_CLOSED);
    yield call(disconnect);
    yield cancel(task);
  }
}

function* boardTranslationsFlow() {
  while (yield take(actions.DIALOG_OPENED)) {
    const task = yield fork(function* () {
      yield all([
        call(load)
      ]);
    })
    yield take(actions.DIALOG_CLOSED);
    yield call(disconnect)
    yield cancel(task);
  }
}

function* refreshTranslatedBoards() {
  yield delay(100);
  yield call(load);
}

/**
 * Init Saga.
 */
function* boardTranslationsSaga() {
  yield all([
    spawn(watchRouter),
    spawn(boardTranslationsFlow),
    spawn(useCaseBoardSettingFlow),
    takeEvery(actions.LOAD, load),
    takeEvery(actions.DISCONNECT, disconnect),
    takeEvery(actions.TRANSLATE_BOARD, translateBoard),
    takeEvery(actions.RETRANSLATE_BOARD, retranslateBoard),
    takeEvery(actions.RE_TRANSLATE_BOARDS, reTranslateBoards),
    takeEvery(actions.TRANSLATE_BOARD_DONE, refreshTranslatedBoards),
    takeEvery(actions.RETRANSLATE_BOARD_DONE, refreshTranslatedBoards),
    takeEvery(actions.RE_TRANSLATE_BOARDS_DONE, refreshTranslatedBoards),
    takeEvery(actions.UPDATE_USE_CASE_BOARD_SETTINGS, updateUseCaseBoardSettings),
  ]);
}

export default boardTranslationsSaga;